import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Package, ShoppingBag, Star, DollarSign, LogOut, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [product, setProduct] = useState(null);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bulkPricing, setBulkPricing] = useState({ tiers: [] });
  
  // Product form
  const [productForm, setProductForm] = useState({
    stock: 0,
    price: 0
  });

  // Review form
  const [reviewForm, setReviewForm] = useState({
    name: '',
    rating: 5,
    comment: '',
    date: new Date().toISOString().split('T')[0],
    featured: false
  });

  // Bulk pricing form
  const [newTier, setNewTier] = useState({
    min_quantity: 500,
    max_quantity: 999,
    price_per_unit: 12.99
  });

  useEffect(() => {
    verifyAuth();
    fetchAllData();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('admin_token');
      setIsAuthenticated(false);
      navigate('/admin/login');
    }
  };

  const fetchAllData = async () => {
    await fetchProduct();
    await fetchOrders();
    await fetchReviews();
    await fetchBulkPricing();
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      if (response.data.length > 0) {
        const prod = response.data[0];
        setProduct(prod);
        setProductForm({
          stock: prod.stock,
          price: prod.price
        });
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchBulkPricing = async () => {
    try {
      const response = await axios.get(`${API}/bulk-pricing`);
      setBulkPricing(response.data);
    } catch (error) {
      console.error('Error fetching bulk pricing:', error);
    }
  };

  const handleUpdateProduct = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/products/${product.id}`,
        productForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Product updated successfully!');
      fetchProduct();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated!');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleAddReview = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/reviews`,
        reviewForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review added successfully!');
      setReviewForm({
        name: '',
        rating: 5,
        comment: '',
        date: new Date().toISOString().split('T')[0],
        featured: false
      });
      fetchReviews();
    } catch (error) {
      console.error('Error adding review:', error);
      toast.error('Failed to add review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API}/reviews/${reviewId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review deleted!');
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const handleUpdateBulkPricing = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/bulk-pricing`,
        { tiers: bulkPricing.tiers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bulk pricing updated!');
      fetchBulkPricing();
    } catch (error) {
      console.error('Error updating bulk pricing:', error);
      toast.error('Failed to update bulk pricing');
    }
  };

  const handleAddTier = () => {
    setBulkPricing({
      ...bulkPricing,
      tiers: [...bulkPricing.tiers, newTier]
    });
    setNewTier({
      min_quantity: 500,
      max_quantity: 999,
      price_per_unit: 12.99
    });
  };

  const handleRemoveTier = (index) => {
    const newTiers = bulkPricing.tiers.filter((_, i) => i !== index);
    setBulkPricing({ ...bulkPricing, tiers: newTiers });
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#D4A574' }}></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#fafafa' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
              alt="ONYX Logo" 
              className="h-20"
            />
            <span className="text-2xl font-bold" style={{ color: '#1a2332' }}>Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="outline" data-testid="view-store-button">
              View Store
            </Button>
            <Button onClick={handleLogout} variant="outline" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-white border border-gray-200 rounded-xl">
            <TabsTrigger value="products" className="py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-50 data-[state=active]:to-amber-100" data-testid="products-tab">
              <Package className="w-4 h-4 mr-2" /> Products
            </TabsTrigger>
            <TabsTrigger value="orders" className="py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-50 data-[state=active]:to-amber-100" data-testid="orders-tab">
              <ShoppingBag className="w-4 h-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="reviews" className="py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-50 data-[state=active]:to-amber-100" data-testid="reviews-tab">
              <Star className="w-4 h-4 mr-2" /> Reviews
            </TabsTrigger>
            <TabsTrigger value="pricing" className="py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-50 data-[state=active]:to-amber-100" data-testid="pricing-tab">
              <DollarSign className="w-4 h-4 mr-2" /> Bulk Pricing
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="p-8 bg-white rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a2332' }}>Manage Product</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-bold mb-2 block">Product Name</Label>
                  <p className="text-gray-600">{product.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-bold mb-2 block">Current Stock</Label>
                  <Input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) || 0 })}
                    className="py-6 rounded-xl"
                    data-testid="product-stock-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold mb-2 block">Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                    className="py-6 rounded-xl"
                    data-testid="product-price-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleUpdateProduct}
                className="mt-6 px-8 py-6 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                data-testid="update-product-button"
              >
                Update Product
              </Button>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <div className="p-8 bg-white rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a2332' }}>Orders ({orders.length})</h2>
              {orders.length === 0 ? (
                <p className="text-gray-600">No orders yet</p>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="p-6 border border-gray-200 rounded-xl">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="font-bold" style={{ color: '#1a2332' }}>{order.customer_name}</p>
                          <p className="text-sm text-gray-500">{order.customer_email}</p>
                          <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{ color: '#1a2332' }}>${order.total.toFixed(2)}</p>
                          <select
                            value={order.status}
                            onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                            className="mt-2 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                            data-testid={`order-status-${order.id}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-6">
            <div className="p-8 bg-white rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a2332' }}>Add New Review</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-sm font-bold mb-2 block">Name</Label>
                  <Input
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                    className="py-6 rounded-xl"
                    placeholder="Customer name"
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold mb-2 block">Rating</Label>
                  <select
                    value={reviewForm.rating}
                    onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })}
                    className="w-full py-3 px-4 border border-gray-300 rounded-xl"
                    data-testid="review-rating-select"
                  >
                    {[5, 4, 3, 2, 1].map(n => (
                      <option key={n} value={n}>{n} Stars</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-bold mb-2 block">Comment</Label>
                  <Textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    className="rounded-xl min-h-[120px]"
                    placeholder="Review comment"
                    data-testid="review-comment-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold mb-2 block">Date</Label>
                  <Input
                    type="date"
                    value={reviewForm.date}
                    onChange={(e) => setReviewForm({ ...reviewForm, date: e.target.value })}
                    className="py-6 rounded-xl"
                    data-testid="review-date-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={reviewForm.featured}
                    onChange={(e) => setReviewForm({ ...reviewForm, featured: e.target.checked })}
                    className="w-5 h-5"
                    data-testid="review-featured-checkbox"
                  />
                  <Label className="text-sm font-bold">Featured Review</Label>
                </div>
              </div>
              <Button
                onClick={handleAddReview}
                className="px-8 py-6 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                data-testid="add-review-button"
              >
                Add Review
              </Button>
            </div>

            <div className="p-8 bg-white rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a2332' }}>All Reviews ({reviews.length})</h2>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="p-6 border border-gray-200 rounded-xl flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-bold" style={{ color: '#1a2332' }}>{review.name}</p>
                        <div className="flex">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-current" style={{ color: '#D4A574' }} />
                          ))}
                        </div>
                        {review.featured && (
                          <span className="px-3 py-1 text-xs font-bold rounded-full" style={{ background: '#fef3e4', color: '#8B6F47' }}>
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{review.comment}</p>
                      <p className="text-sm text-gray-500">{review.date}</p>
                    </div>
                    <Button
                      onClick={() => handleDeleteReview(review.id)}
                      variant="outline"
                      size="sm"
                      className="ml-4"
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Bulk Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6">
            <div className="p-8 bg-white rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a2332' }}>Manage Bulk Pricing</h2>
              
              <div className="space-y-4 mb-6">
                {bulkPricing.tiers.map((tier, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-xl flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Min Quantity</Label>
                        <Input
                          type="number"
                          value={tier.min_quantity}
                          onChange={(e) => {
                            const newTiers = [...bulkPricing.tiers];
                            newTiers[index].min_quantity = parseInt(e.target.value) || 0;
                            setBulkPricing({ ...bulkPricing, tiers: newTiers });
                          }}
                          className="mt-1"
                          data-testid={`tier-min-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Max Quantity</Label>
                        <Input
                          type="number"
                          value={tier.max_quantity || ''}
                          onChange={(e) => {
                            const newTiers = [...bulkPricing.tiers];
                            newTiers[index].max_quantity = e.target.value ? parseInt(e.target.value) : null;
                            setBulkPricing({ ...bulkPricing, tiers: newTiers });
                          }}
                          className="mt-1"
                          placeholder="∞"
                          data-testid={`tier-max-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Price per Unit ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={tier.price_per_unit}
                          onChange={(e) => {
                            const newTiers = [...bulkPricing.tiers];
                            newTiers[index].price_per_unit = parseFloat(e.target.value) || 0;
                            setBulkPricing({ ...bulkPricing, tiers: newTiers });
                          }}
                          className="mt-1"
                          data-testid={`tier-price-${index}`}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRemoveTier(index)}
                      variant="outline"
                      size="sm"
                      className="ml-4"
                      data-testid={`remove-tier-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl mb-6">
                <h3 className="font-bold mb-4" style={{ color: '#1a2332' }}>Add New Tier</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Min Quantity</Label>
                    <Input
                      type="number"
                      value={newTier.min_quantity}
                      onChange={(e) => setNewTier({ ...newTier, min_quantity: parseInt(e.target.value) || 0 })}
                      className="mt-1"
                      data-testid="new-tier-min"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Max Quantity</Label>
                    <Input
                      type="number"
                      value={newTier.max_quantity}
                      onChange={(e) => setNewTier({ ...newTier, max_quantity: parseInt(e.target.value) || 0 })}
                      className="mt-1"
                      data-testid="new-tier-max"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Price per Unit ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newTier.price_per_unit}
                      onChange={(e) => setNewTier({ ...newTier, price_per_unit: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                      data-testid="new-tier-price"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddTier}
                  variant="outline"
                  className="mt-4"
                  data-testid="add-tier-button"
                >
                  Add Tier
                </Button>
              </div>

              <Button
                onClick={handleUpdateBulkPricing}
                className="px-8 py-6 rounded-xl"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                data-testid="update-bulk-pricing-button"
              >
                Save Bulk Pricing
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
